import React from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Avatar, Typography, Toolbar, Badge } from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext'; // Import context mới
import { usePendingRequests } from '../../contexts/PendingRequestsContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TaskIcon from '@mui/icons-material/Task';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CategoryIcon from '@mui/icons-material/Category';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SettingsIcon from '@mui/icons-material/Settings';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PaymentsIcon from '@mui/icons-material/Payments';
import MoreTimeIcon from '@mui/icons-material/MoreTime';

const SidebarContent = () => {
  const { currentUser, logout } = useAuth();
  const { isShareScheduleEnabled } = useSettings(); // Sử dụng context
  const { pendingRequestsCount, pendingOvertimeCount } = usePendingRequests();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error("Lỗi đăng xuất", error);
    }
  };

  const menuItems = [
    { text: 'Bảng điều khiển', path: '/', icon: <DashboardIcon />, roles: ['admin', 'senior_manager', 'employee', 'manager'] },
    { text: 'Chấm công của tôi', path: '/my-attendance', icon: <AccessTimeIcon />, roles: ['employee', 'senior_manager', 'manager'] },
    { text: 'Lịch tổng hợp', path: '/schedule', icon: <CalendarMonthIcon />, roles: ['admin', 'senior_manager', 'manager'] },
    { text: 'Đăng ký lịch', path: '/schedule', icon: <CalendarMonthIcon />, roles: ['employee'] },
    { text: 'Công việc của tôi', path: '/my-tasks', icon: <TaskIcon />, roles: ['employee', 'senior_manager', 'manager'] },
    { text: 'Điều khoản làm việc', path: '/work-terms', icon: <MenuBookIcon />, roles: ['employee', 'senior_manager', 'manager'] },
    { text: 'Thu nhập', path: '/income', icon: <AccountBalanceWalletIcon />, roles: ['employee', 'senior_manager', 'manager'] },
    { text: 'Báo cáo', path: '/report', icon: <AssessmentIcon />, roles: ['admin', 'senior_manager', 'employee', 'manager'] },
    { text: 'Quản trị', path: '/admin', icon: <AdminPanelSettingsIcon />, roles: ['admin', 'senior_manager'] },
    { text: 'Quản lý yêu cầu', path: '/admin/requests', icon: <NotificationsIcon />, roles: ['admin', 'senior_manager', 'manager'] },
    { text: 'Làm thêm giờ', path: '/admin/overtime', icon: <MoreTimeIcon />, roles: ['admin', 'senior_manager', 'manager'] },
    { text: 'Quản lý công việc', path: '/admin/tasks', icon: <AssignmentIcon />, roles: ['admin', 'senior_manager'] },
    { text: 'Loại công việc', path: '/admin/task-types', icon: <CategoryIcon />, roles: ['admin', 'senior_manager'] },
    { text: 'Tính lương', path: '/admin/salary', icon: <AttachMoneyIcon />, roles: ['admin'] }, // Chỉ admin, không có senior_manager
    { text: 'Thanh toán', path: '/admin/payment', icon: <PaymentsIcon />, roles: ['admin'] }, // Chỉ admin: liên quan tới tài khoản ngân hàng
    { text: 'Danh sách chấm công', path: '/admin/attendance', icon: <AccessTimeIcon />, roles: ['admin', 'senior_manager'] },
    { text: 'Đường đua nhân viên', path: '/admin/race-track', icon: <EmojiEventsIcon />, roles: ['admin', 'senior_manager'] },
    { text: 'Cài đặt chấm công', path: '/admin/attendance-system', icon: <SettingsIcon />, roles: ['admin', 'senior_manager'] },
    { text: 'Tài khoản', path: '/profile', icon: <AccountCircleIcon />, roles: ['admin', 'senior_manager', 'employee', 'manager'] },
    { text: 'Cài đặt thông báo', path: '/notification-settings', icon: <NotificationsIcon />, roles: ['admin', 'senior_manager', 'employee', 'manager'] },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box>
        <Toolbar />
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Avatar src={currentUser?.photoURL} sx={{ width: 64, height: 64, margin: 'auto' }} />
          <Typography variant="h6" sx={{ mt: 1, wordBreak: 'break-word' }}>{currentUser?.displayName}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{currentUser?.email}</Typography>
        </Box>
        <Divider />
        <List>
          {menuItems.map((item) => {
            if (!item.roles.includes(currentUser?.role)) {
              return null;
            }

            // Kiểm tra điều kiện hiển thị (nếu có)
            if (item.condition !== undefined && !item.condition(currentUser?.role, { isShareScheduleEnabled, currentUser })) {
              return null;
            }

            return (
              <ListItem key={item.text} disablePadding>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={location.pathname === item.path || (item.path === '/income' && location.pathname.startsWith('/income'))}
                >
                  <ListItemIcon>
                    {item.text === 'Quản lý yêu cầu' && (currentUser?.role === 'admin' || currentUser?.role === 'manager') ? (
                      <Badge badgeContent={pendingRequestsCount} color="error">
                        {item.icon}
                      </Badge>
                    ) : item.path === '/admin/overtime' ? (
                      <Badge badgeContent={pendingOvertimeCount} color="secondary">
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Box>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon><LogoutIcon /></ListItemIcon>
              <ListItemText primary="Đăng xuất" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  );
};

export default SidebarContent;
